/**
 * Who may write a COMPANY-tier row, as the frontend understands it.
 *
 * ── The backend rule this mirrors ───────────────────────────────────────────
 * `apps/api/src/auth/tenancy/tenancy-authorization.ts` refuses a company-tier
 * write from anyone whose `users.role` is not `Company Owner` / `Company Admin`
 * — it reads the ROLE column only, never `permissions`, and never the workspace
 * role. RC-I (2026-09-20) put that gate on eight surfaces that previously took
 * the tier from the request itself:
 *
 *   PUT   /v1/lms/settings/company-defaults
 *   PUT   /v1/esa/settings/company-defaults
 *   PUT   /v1/phishtrainer/settings/company-defaults
 *   PUT   /v1/portal-settings/company-default
 *   PATCH /v1/team-portal-settings/company-default
 *   PUT   /v1/er/settings/addin-branding/company-default
 *   POST  /v1/lms/certificate-templates/:id/set-default-explicit (scope=Company)
 *   POST/PATCH/DELETE /v1/brand-logos (scope=Company rows)
 *
 * ── Why the frontend gets an opinion here ───────────────────────────────────
 * Every one of those surfaces is reachable today by a Workspace Admin — the
 * page is in the nav, the form loads (the READ side is deliberately still open,
 * because a workspace must be able to SEE what it inherits), and before this
 * predicate existed the only feedback was a red toast on Save with the form
 * still showing the operator's unsaved values. On an MSSP tenant the Workspace
 * Admin is often the ONLY person administering a module, so this is the
 * difference between "read-only, ask a Company Admin" and a support call.
 *
 * Three properties keep it honest, and they are the same three
 * `lib/ai/key-scope.ts` records for the identical problem one module over:
 *
 *   1. It is NOT the security boundary. The server refuses the write whatever
 *      this returns; getting it wrong disables a control that would have
 *      failed, never enables one that would have succeeded.
 *   2. It FAILS CLOSED. An unknown, absent or unrecognised role is not company
 *      tier. Guessing "yes" is what produces the dead-end save.
 *   3. It is one function. Four hand-rolled copies of this predicate already
 *      existed in `apps/web` (key-scope.ts, transport-sections.tsx,
 *      transport-detail-panel.tsx, add-credential-modal.tsx) — which is the
 *      frontend half of exactly the reason the backend copies let eight writes
 *      ship ungated: no single place said what the rule was.
 */

import { useAuthStore } from '@/stores/auth-store'

/**
 * The `user_role` values the API treats as company tier. Kept in step with
 * `USER_ROLE_RANK` / `COMPANY_TIER_RANK` in
 * `apps/api/src/auth/tenancy/role-assignment.ts`;
 * `lib/__tests__/company-tier-write-sites.guard.spec.ts` parses that file and
 * fails if the two drift.
 */
export const COMPANY_TIER_ROLES: readonly string[] = [
  'Company Owner',
  'Company Admin',
]

/** True only for a role the API would accept for a company-tier write. */
export function isCompanyTierRole(role: string | null | undefined): boolean {
  if (typeof role !== 'string') return false
  return COMPANY_TIER_ROLES.includes(role.trim())
}

/**
 * The one sentence every company-tier surface shows a workspace-tier operator,
 * so the reason a control is disabled is on the page rather than in a toast.
 */
export const COMPANY_TIER_READ_ONLY_HINT =
  'These defaults apply to every workspace in the company, so only a Company Owner or Company Admin can change them. You can see what your workspace inherits here.'

/**
 * Hook form, for client components. Reads the signed-in user's role from the
 * auth store; returns false while the store is empty (fails closed).
 */
export function useIsCompanyTier(): boolean {
  const role = useAuthStore((s) => s.user?.role)
  return isCompanyTierRole(role)
}

/**
 * For a company-tier surface the API will not even READ for a workspace-tier
 * operator — Email Delivery's Company scope, whose authorization refuses read
 * and write alike (`email-delivery-settings.authorization.ts`). There is
 * nothing to show read-only there, so the card is replaced by this sentence
 * instead of rendering "Couldn't load settings" (2026-09-23 settings audit #13).
 */
export const COMPANY_TIER_HIDDEN_HINT =
  'Company-wide defaults are managed by a Company Owner or Company Admin. The workspace settings on this page show what your workspace inherits from them.'
